import React, { useEffect, useRef, useState } from "react";

/**
 * ScrollReveal — reveals children with a smooth fade/slide-up when they
 * enter the viewport. Uses IntersectionObserver, one-shot per element.
 *
 * Props:
 *  - as: element/tag (default: "div")
 *  - delay: ms before animation starts (default: 0)
 *  - direction: "up" | "down" | "left" | "right" | "none" (default: "up")
 *  - distance: px translate amount (default: 32)
 *  - duration: ms (default: 700)
 *  - once: only fire once (default: true)
 */
export default function ScrollReveal({
  as: Tag = "div",
  delay = 0,
  direction = "up",
  distance = 32,
  duration = 700,
  once = true,
  className = "",
  style = {},
  children,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect users who prefer reduced motion
    const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) obs.unobserve(e.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);

  const translate = (() => {
    if (!visible) {
      switch (direction) {
        case "up":
          return `translate3d(0, ${distance}px, 0)`;
        case "down":
          return `translate3d(0, -${distance}px, 0)`;
        case "left":
          return `translate3d(${distance}px, 0, 0)`;
        case "right":
          return `translate3d(-${distance}px, 0, 0)`;
        default:
          return "none";
      }
    }
    return "translate3d(0, 0, 0)";
  })();

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: translate,
        transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: "opacity, transform",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
